import update from "immutability-helper";
import moment from "moment";
import React from "react";
import { Modal } from "../../../components";
import { validateSegment } from "../../flightvalidator";
import { type CommercialFlightEditProps, getDefaultSegment, type PrivateFlightEditProps, type Segment } from "../../model";
import RetrieveFlights from "../retrieveflights/retrieveflights";
import SegmentEdit from "../segmentedit/segmentedit";
import EditViewPanel from "components/panels/EditViewPanel";
import { ButtonSecondary } from "components/Atoms/Button";

interface Props {
  flight: server.dto.Flight
  initialSegments: server.dto.FlightSegment[]
  onSave: (newflight: server.dto.Flight) => void
  onModalClose: () => void
  frequentFlyerPrograms: server.dto.FrequentFlyerProgram[]
}

interface State {
  segments: Record<number, Segment>
  showFlightlookup: boolean
}

export default class AddNewSegmentsPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    if (this.props.initialSegments.length === 0) {
      this.state = {
        segments: {
          0: {
            props: this.getDefaultSegmentFromLast(),
            type: "commercial",
            noAirlineError: false,
            noArrivalAirportError: false,
            noDepartureAirportError: false,
            noFlightCodeError: false,
            noFlightNumberError: false,
            departureTooEarly: false
          }
        },
        showFlightlookup: false
      };
    } else {
      this.state = {
        segments: this.mapSegments(props.initialSegments),
        showFlightlookup: false
      };
    }
  }

  getNewIndex = (): number => {
    const ids = Object.keys(this.state.segments);
    return ids.length;
  };

  mapSegments = (segments: server.dto.FlightSegment[]): Record<number, Segment> => {
    const lastIndex = this.state ? this.getNewIndex() : 0;

    return segments.map(s => ({
      type: "commercial",
      props: {
        arrival: moment.utc(s.arrival.dateTimeLocal),
        departure: moment.utc(s.departure.dateTimeLocal),

        arrivalAirport: s.arrival.airport,
        departureAirport: s.departure.airport,

        arrivalTerminal: s.arrival.terminal || "",
        departureTerminal: s.departure.terminal || "",

        arrivalGate: s.arrival.gate || "",
        departureGate: s.departure.gate || "",

        airline: s.airline,

        flightNumber: s.flightDesignator.number.toString() || "",
        flightCode: s.flightDesignator.code || "",

        aircraft: s.aircraft || "",

        seats: s.seats || "",
        travelClass: s.travelClass,
        eticket: s.eTicket || "",

        access: "",
        confirmationNumber: "",
        capacity: ""
      }
    }))
      .reduce((acc, curr, idx) =>
        ({ ...acc, [idx + lastIndex]: curr }) // merge acc with the new obj, e.g. {1: {}} can become {1: {}, 2: {}}
      , {});
  };

  onExistingFlightEntry = (segments: server.dto.FlightSegment[]) => {
    const newSegments = this.mapSegments(segments);

    this.setState({ segments: { ...this.state.segments, ...newSegments }, showFlightlookup: false });
  };

  onTypeChange = (newType: server.dto.FlightTypes, index: number) => {
    const newState = update(this.state, {
      segments: {
        [index]: {
          type: { $set: newType }
        }
      }
    });

    this.setState(newState);
  };

  onAddSegmentClick = () => {
    this.setState({ showFlightlookup: true });
  };

  onSegmentEdited = (segment: CommercialFlightEditProps & PrivateFlightEditProps, index: number) => {
    const newState = update(this.state, {
      segments: {
        [index]: {
          props: { $set: segment }
        }
      }
    });
    this.setState(newState);
  };

  getDefaultSegmentFromLast = (): CommercialFlightEditProps & PrivateFlightEditProps => {
    const newSegment = getDefaultSegment();

    if (this.state && Object.keys(this.state.segments).length > 0) {
      const keys = Object.keys(this.state.segments);
      const lastKey = Number(keys[keys.length - 1]);
      const lastItem = this.state.segments[lastKey];

      newSegment.departureAirport = lastItem.props.arrivalAirport;
      newSegment.airline = lastItem.props.airline;
      newSegment.flightCode = lastItem.props.flightCode;
      newSegment.departure = lastItem.props.arrival;
      newSegment.arrival = lastItem.props.arrival;
    } else if (this.props.flight.segments.length > 0) {
      const lastSegment = this.props.flight.segments[this.props.flight.segments.length - 1];

      newSegment.departure = moment.utc(lastSegment.arrival.dateTimeLocal);
      newSegment.arrival = newSegment.departure;
      newSegment.departureAirport = lastSegment.arrival.airport;
      newSegment.flightCode = lastSegment.flightDesignator.code;
      newSegment.airline = lastSegment.airline;
    }

    return newSegment;
  };

  validate = (): boolean => {
    const keys = Object.keys(this.state.segments);

    let anyErrors = false;

    const result = keys.reduce<any>((acc: Record<number, Segment>, current: string): Record<number, Segment> => {
      const segment = this.state.segments[Number(current)];

      const validationResult = validateSegment(segment);

      anyErrors = anyErrors || validationResult.anyErrors();

      return {
        ...acc,
        [Number(current)]: {
          ...segment,
          ...validationResult.errors
        }
      };
    }, {});

    this.setState({ segments: result });

    return anyErrors;
  };

  onManualEntry = () => {
    const newState = update(this.state, {
      segments: {
        [this.getNewIndex()]: {
          $set: {
            props: this.getDefaultSegmentFromLast(),
            type: "commercial",
            noFlightNumberError: false,
            noFlightCodeError: false,
            noDepartureAirportError: false,
            noArrivalAirportError: false,
            noAirlineError: false,
            departureTooEarly: false
          }
        }
      },
      showFlightlookup: { $set: false }
    });

    this.setState(newState);
  };

  onSaveClick = async () => {
    if (this.validate()) {
      return;
    }

    const flight = update(this.props.flight, {
      segments: {
        $push:
                    Object.keys(this.state.segments).map(key => {
                      const item = this.state.segments[Number(key)];
                      const { props } = item;
                      return {
                        id: undefined,
                        departure: {
                          dateTimeLocal: props.departure.toDate(),
                          airport: props.departureAirport,
                          terminal: props.departureTerminal,
                          gate: props.departureGate
                        },
                        arrival: {
                          dateTimeLocal: props.arrival.toDate(),
                          airport: props.arrivalAirport,
                          terminal: props.arrivalTerminal,
                          gate: props.arrivalGate
                        },
                        type: item.type,
                        flightDesignator: {
                          code: props.flightCode,
                          number: Number(props.flightNumber)
                        },
                        eTicket: props.eticket,
                        airline: props.airline,
                        flightNumber: props.flightNumber,
                        aircraft: props.aircraft,
                        travelClass: props.travelClass,
                        seats: props.seats,
                        frequentFlyerProgram: props.frequentFlyer,
                        capacity: {
                          value: props.capacity,
                          formatted: ""
                        },
                        confirmationNumber: "",
                        access: "",
                        linkedShows: []
                      } as any;
                    })
      } as any
    });

    const result = await window.Api.Flights.UpdateFlight(flight.id, flight);

    this.props.onSave(result.data);
  };

  public render(): JSX.Element {
    return <>
      <Modal
        handleClose={this.props.onModalClose}
        title={RESX.Flight.resxFlight}
        isOpen
        width={"large"}
        hasAutoScrollBox
        headerButton={<ButtonSecondary onClick={this.onAddSegmentClick} label={RESX.Flight.AddSegment} />}
      >
        <EditViewPanel
          title={RESX.Flight.resxFlight}
          className="grow1 scrollbox-auto">
          {Object.values(this.state.segments).map((segment, idx) => (
            <SegmentEdit
              key={idx}
              editedSegment={segment.props}
              onSegmentEdited={this.onSegmentEdited}
              index={idx}
              type={segment.type}
              onTypeChanged={this.onTypeChange}
              frequentFlyerPrograms={this.props.frequentFlyerPrograms}
              noAirlineError={segment.noAirlineError}
              noArrivalAirportError={segment.noArrivalAirportError}
              noDepartureAirportError={segment.noDepartureAirportError}
              noFlightCodeError={segment.noFlightCodeError}
              noFlightNumberError={segment.noFlightNumberError}
              departureTooEarly={segment.departureTooEarly}
            />
          ))}
        </EditViewPanel>

        <div className="flex margin-top-large justify-end">
          <div>
            <button type="button" onClick={this.onSaveClick} className="button-new primary" value={RESX.Buttons.btnSave} name="Save">
              {RESX.Buttons.btnSave}
            </button>
          </div>
        </div>
      </Modal>

      {this.state.showFlightlookup && (
        <Modal
          title={RESX.Flight.FlightLookup}
          width={"large"}
          minHeight={350}
          height={600}
          isOpen={this.state.showFlightlookup}
          handleClose={() => { this.setState({ showFlightlookup: false }); }}
        >
          <RetrieveFlights
            onManualEntryClicked={this.onManualEntry}
            onSelectWithSegmentClicked={this.onExistingFlightEntry}
          />
        </Modal>
      )}
    </>;
  }
}
